/*
    TrackingEventFactory: builds Tracking Events.
 */

import moment from "moment-timezone";

import State from "../../State";
import EventType from "../../EventType";
import Tracker from "../../Tracker";
import WorkflowTransition from "../../TrackingEventWorkflowTransition";

import CurrentTimeZoneServiceModule from "../../../services/CurrentTimeZoneService";
import AllStatesServiceModule from "../../../services/AllStatesService";
import AllTrackingStatusesServiceModule from "../../../services/AllTrackingStatusesService";
import TrackingTargetFactoryModule from "../TrackingTargetFactory";

import TrackingEvent from "../../TrackingEvent";

//Private factory members
const mCurrentTimeZoneSvc = new WeakMap();
const mAllStatesService = new WeakMap();
const mAllTrackingStatusesSvc = new WeakMap();
const mTrackingTargetFactory = new WeakMap();

class TrackingEventFactory {
    constructor(
        CurrentTimeZone,
        AllStatesService,
        AllTrackingStatuses,
        TrackingTargetFactory
    ) {
        if (CurrentTimeZone != null) {
            mCurrentTimeZoneSvc.set(this, CurrentTimeZone);
        }
        else {
            throw new Error("Invalid or missing current time zone dependency");
        }

        if (AllStatesService != null) {
            mAllStatesService.set(this, AllStatesService);
        }
        else {
            throw new Error("Invalid or missing all states service dependency");
        }

        if (AllTrackingStatuses != null) {
            mAllTrackingStatusesSvc.set(this, AllTrackingStatuses);
        }
        else {
            throw new Error("Invalid or missing all tracking statuses service dependency");
        }

        if (TrackingTargetFactory != null) {
            mTrackingTargetFactory.set(this, TrackingTargetFactory);
        }
        else {
            throw new Error("Invalid or missing tracking target factory service dependency");
        }
    }
    
    Create() {
        return TrackingEvent.Create(
            mAllStatesService.get(this),
            mAllTrackingStatusesSvc.get(this),
            mTrackingTargetFactory.get(this)
        );
    }

    CreateNew() {

        let getNearestMoment = function (intervalAmount) {
            let cTime = moment.utc();
            let cMins = cTime.minute();
            if ((cMins % intervalAmount) !== 0) {
                let minsAdjust = intervalAmount - (cMins % intervalAmount);
                cTime.add(minsAdjust, "minutes");
            }

            return cTime;
        };

        let startMoment = getNearestMoment(15);
        let endMoment = moment(startMoment).add(1, "hours");

        let tEvent = new TrackingEvent(
            mAllStatesService.get(this),
            mAllTrackingStatusesSvc.get(this),
            0,
            "",
            startMoment,
            endMoment,
            mCurrentTimeZoneSvc.get(this).get(),
            "",
            "",
            "",
            null, //initialize status to start point
            false, // Initialize as a non-internal event
            false, // Initialize as a non-external event
            "",
            "",
            "",
            mAllStatesService.get(this).FromFullName("Alabama"),
            [], //there are no current workflow steps
            [], //no current event types assigned
            [], //no trackers assigned yet
            [], //no attendees yet
            null,
            null,
            null,
            false
        );

        return tEvent;
    }

    ParseServerEvent(serverTrackingEvent) {

        let eventLocationState = State.FromServerState(
            serverTrackingEvent.Location_State
        );

        let eventWorkflowTransitions =
            serverTrackingEvent.WorkflowLinks.map((currentWfEntry) => new WorkflowTransition(
                currentWfEntry.Transition.Rel,
                currentWfEntry.Transition.Href,
                currentWfEntry.Transition.Text,
                ((currentWfEntry.CanPreCheckTransition === true) ? currentWfEntry.PreCheckTransition.Href : null)
            ));

        let evtTypes =
            serverTrackingEvent.EventTypes.map((serverET) => EventType.FromServerEventType(serverET));

        let eventTrackers =
            serverTrackingEvent.AssignedTrackers.map(function(serverTracker) {
                return Tracker.FromServerTracker(serverTracker);
            });

        let eventAttendees = serverTrackingEvent.EventAttendees.map(
            (serverTgt) => mTrackingTargetFactory.get(this).FromServerTrackingTarget(serverTgt));

        let tEvent = new TrackingEvent(
            mAllStatesService.get(this),
            mAllTrackingStatusesSvc.get(this),
            serverTrackingEvent.TrackingEventId,
            serverTrackingEvent.Name,
            serverTrackingEvent.EventStart,
            serverTrackingEvent.EventEnd,
            serverTrackingEvent.StartTimezone, //Start Timezone->event timezone
            serverTrackingEvent.Description,
            serverTrackingEvent.Notes,
            serverTrackingEvent.PressDetails,
            serverTrackingEvent.Status,
            serverTrackingEvent.IsInternal,
            serverTrackingEvent.IsExternal,
            serverTrackingEvent.Location_Name,
            serverTrackingEvent.Location_Address,
            serverTrackingEvent.Location_City,
            eventLocationState,
            eventWorkflowTransitions,
            evtTypes,
            eventTrackers,
            eventAttendees,
            serverTrackingEvent.RecurrenceID,
            serverTrackingEvent.RecurrenceRule,
            serverTrackingEvent.RecurrenceException,
            serverTrackingEvent.IsAllDay
        );

        return tEvent;
    }

    ParseSchedulerEventData(schedulerEvent) {
        let updateRequestState = mAllStatesService.get(this).FromId(
            schedulerEvent.locationStateId
        );

        if (updateRequestState == null) {
            throw new Error("Parse Scheduler Event Data: Invalid State");
        }

        let tEvent = new TrackingEvent(
            schedulerEvent.trackingEventId,
            schedulerEvent.title,
            schedulerEvent.start,
            schedulerEvent.end,
            schedulerEvent.eventTimezone,
            schedulerEvent.description,
            schedulerEvent.notes,
            schedulerEvent.pressDetails,
            schedulerEvent.statusName,
            schedulerEvent.isInternal,
            schedulerEvent.isExternal,
            schedulerEvent.locationName,
            schedulerEvent.locationAddress,
            schedulerEvent.locationCity,
            updateRequestState,
            // We can ignore scheduler's view of:
            [], // Workflow transition data
            [], // Event types.
            [], // Event trackers.
            [], // Event attendees.
            schedulerEvent.recurrenceId,
            schedulerEvent.recurrenceRule,
            schedulerEvent.recurrenceException,
            schedulerEvent.isAllDay
        );

        return tEvent;
    }
}

TrackingEventFactory.$inject = [ "CurrentTimeZone", "AllStatesService", "AllTrackingStatuses", "TrackingTargetFactory" ];

TrackingEventFactory.dependencies = [
    CurrentTimeZoneServiceModule,
    AllTrackingStatusesServiceModule,
    AllStatesServiceModule,
    TrackingTargetFactoryModule
];

export default TrackingEventFactory;



//////////////////
// WEBPACK FOOTER
// ./src/models/factories/tracking-event-factories/TrackingEventFactory.js
// module id = zmPc
// module chunks = 1